export class Point {
  x: number;
  y: number;

  get rounded() {
    return new Point(Math.round(this.x), Math.round(this.y));
  }

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  clone() {
    return new Point(this.x, this.y);
  }

  eq(point: Point | undefined) {
    if (!point) return false;
    return this.x === point.x && this.y === point.y;
  }

  offset(offset: Size) {
    return new Point(this.x + offset.width, this.y + offset.height);
  }

  offsetX(x: number) {
    return new Point(this.x + x, this.y);
  }

  offsetY(y: number) {
    return new Point(this.x, this.y + y);
  }
}

export class Rect {
  origin: Point;
  size: Size;

  get bl() {
    return new Point(this.minX, this.maxY);
  }

  get br() {
    return new Point(this.maxX, this.maxY);
  }

  get h() {
    return this.size.height;
  }

  get height() {
    return this.size.height;
  }

  get maxX() {
    return this.origin.x + this.size.width;
  }

  get maxY() {
    return this.origin.y + this.size.height;
  }

  get minX() {
    return this.origin.x;
  }

  get minY() {
    return this.origin.y;
  }

  get rounded() {
    return new Rect(this.origin.rounded, this.size.rounded);
  }

  get tl() {
    return this.origin;
  }

  get tr() {
    return new Point(this.maxX, this.minY);
  }

  get w() {
    return this.size.width;
  }

  get width() {
    return this.size.width;
  }

  get x() {
    return this.origin.x;
  }

  get y() {
    return this.origin.y;
  }

  constructor(origin: Point, size: Size) {
    this.origin = origin;
    this.size = size;
  }

  clone() {
    return new Rect(this.origin.clone(), this.size.clone());
  }

  contains(rect: Rect) {
    return (
      rect.minY > this.minY &&
      rect.maxY < this.maxY &&
      rect.minX > this.minX &&
      rect.maxX < this.maxX
    );
  }

  eq(rect: Rect | undefined) {
    if (!rect) return false;
    return this.origin.eq(rect.origin) && this.size.eq(rect.size);
  }

  inset(x: number, y: number) {
    return new Rect(
      this.origin.offset(new Size(x, y)),
      new Size(this.size.width - x * 2, this.size.height - y * 2),
    );
  }

  intersects(rect: Rect) {
    return !(
      this.maxX < rect.minX ||
      this.minX > rect.maxX ||
      this.minY > rect.maxY ||
      this.maxY < rect.minY
    );
  }

  intersectsX(rect: Rect) {
    return !(this.maxX < rect.minX || this.minX > rect.maxX);
  }

  intersectsY(rect: Rect) {
    return !(this.minY > rect.maxY || this.maxY < rect.minY);
  }

  offset(offset: Size) {
    return new Rect(this.origin.offset(offset), this.size);
  }

  toString() {
    return `{x:${this.x},y:${this.y},w:${this.w},h:${this.h}}`;
  }

  static from(x: number, y: number, w: number, h: number) {
    return new Rect(new Point(x, y), new Size(w, h));
  }

  static fromDOMRect(rect: DOMRect | DOMRectReadOnly) {
    return new Rect(
      new Point(rect.x, rect.y),
      new Size(rect.width, rect.height),
    );
  }

  static radius(value: number) {
    return { tl: value, tr: value, bl: value, br: value };
  }

  static get zero() {
    return Rect.from(0, 0, 0, 0);
  }
}

export class Size {
  width: number;
  height: number;

  get rounded() {
    return new Size(Math.round(this.width), Math.round(this.height));
  }

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  clone() {
    return new Size(this.width, this.height);
  }

  eq(size: Size | null | undefined) {
    if (!size) return false;
    return this.width === size.width && this.height === size.height;
  }
}

export type PathCommand = {
  curve?: [Point, Point, Point];
  line?: Point;
  move?: Point;
  quadCurve?: [Point, Point];
};

/* c8 ignore start */
export function draw(
  commands: PathCommand[],
  context: CanvasRenderingContext2D,
) {
  context.beginPath();
  for (const command of commands) {
    const { curve, line, move, quadCurve } = command;
    if (curve) {
      const [to, c1, c2] = curve;
      context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, to.x, to.y);
      continue;
    }
    if (line) {
      context.lineTo(line.x, line.y);
      continue;
    }
    if (move) {
      context.moveTo(move.x, move.y);
      continue;
    }
    if (quadCurve) {
      const [to, c1] = quadCurve;
      context.quadraticCurveTo(c1.x, c1.y, to.x, to.y);
      continue;
    }
  }
  context.closePath();
}
/* c8 ignore end */

export function roundedRect(
  rect: Rect,
  radius: { tl: number; tr: number; bl: number; br: number },
): PathCommand[] {
  const result: PathCommand[] = [];
  const { x, y } = rect.origin;
  const { width, height } = rect.size;
  result.push({ move: new Point(x + radius.tl, y) });
  result.push({ line: new Point(x + width - radius.tr, y) });
  result.push({
    quadCurve: [new Point(x + width, y + radius.tr), new Point(x + width, y)],
  });
  result.push({ line: new Point(x + width, y + height - radius.br) });
  result.push({
    quadCurve: [
      new Point(x + width - radius.br, y + height),
      new Point(x + width, y + height),
    ],
  });
  result.push({ line: new Point(x + radius.bl, y + height) });
  result.push({
    quadCurve: [new Point(x, y + height - radius.bl), new Point(x, y + height)],
  });
  result.push({ line: new Point(x, y + radius.tl) });
  result.push({
    quadCurve: [new Point(x + radius.tl, y), new Point(x, y)],
  });
  return result;
}

// Draw a continuous path around a set of text selection rects
// (assumes each rect is on a new line)
//       ┌────────┐
// ┌─────┘  ┌─────┘
// └────────┘
export function textSelectionRects(
  textSelectionRects: Rect[],
  radius: number = 8,
): PathCommand[][] {
  let rects = textSelectionRects.map((rect) => rect.rounded);
  let [a, b] = rects;
  // If there is only 1 rect, just return it
  if (rects.length == 1) {
    return [roundedRect(a, Rect.radius(radius))];
  }
  // If there are only two rects and they don't intersect on the x-axis
  // we should draw two separate paths
  //        ┌─────────┐
  // ┌────┐ └─────────┘
  // └────┘
  if (rects.length == 2 && !a.intersects(b) && !a.intersectsX(b)) {
    return [
      roundedRect(a, Rect.radius(radius)),
      roundedRect(b, Rect.radius(radius)),
    ];
  }
  // Normalize rect widths to prevent weird rounded corners
  // ┌──────┐
  // │     ┌┘
  // └─────┘
  //  ┌────┐
  // ┌┘    │
  // └─────┘
  rects = rects.reduce((rects: Rect[], rect: Rect) => {
    const prev = last(rects);
    if (!prev) return [rect];
    const prevIndex = rects.length - 1;
    const next = rect;
    // Leading
    const a = Math.abs(prev.minX - next.minX);
    if (a <= radius * 1.2) {
      if (prev.minX > next.minX) {
        // Prev
        rects[prevIndex].origin.x -= a;
        rects[prevIndex].size.width += a;
      } else {
        // Next
        next.origin.x -= a;
        next.size.width += a;
      }
    }
    // Trailing
    const b = Math.abs(prev.maxX - next.maxX);
    if (b <= radius * 1.2) {
      if (prev.maxX < next.maxX) {
        // Prev
        rects[prevIndex].size.width += b;
      } else {
        // Next
        next.size.width += b;
      }
    }
    rects.push(next);
    return rects;
  }, []);
  return [curve(outline(rects), radius)];
}

function curve(commands: PathCommand[], radius: number): PathCommand[] {
  const result: PathCommand[] = [];
  const points: Point[] = commands.reduce(
    (points: Point[], command: PathCommand) => {
      const { line, move } = command;
      if (line && !line.eq(last(points))) {
        points.push(line.clone());
      }
      if (move && !move.eq(last(points))) {
        points.push(move.clone());
      }
      return points;
    },
    [],
  );
  let bezierControl: Point | undefined;
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const prevIndex = i === 0 ? points.length - 1 : i - 1;
    const nextIndex = i === points.length - 1 ? 0 : i + 1;
    const prev = points[prevIndex];
    const next = points[nextIndex];
    const isPrevHorizontal = point.y == prev.y;
    const isNextHorizontal = point.y == next.y;
    const prevMultiplier =
      (isPrevHorizontal ? prev.x - point.x : prev.y - point.y) >= 0 ? 1 : -1;
    const nextMultiplier =
      (isNextHorizontal ? next.x - point.x : next.y - point.y) >= 0 ? 1 : -1;
    const prevOffset = new Size(
      isPrevHorizontal ? radius * prevMultiplier : 0,
      isPrevHorizontal ? 0 : radius * prevMultiplier,
    );
    const nextOffset = new Size(
      isNextHorizontal ? radius * nextMultiplier : 0,
      isNextHorizontal ? 0 : radius * nextMultiplier,
    );

    if (i === 0) {
      result.push({ move: point.offsetX(radius) });
      continue;
    }

    if (bezierControl === undefined) {
      result.push({ line: point.offset(prevOffset) });
    }

    if (
      isNextHorizontal &&
      !point.eq(next) &&
      Math.abs(point.x - next.x) < radius * 2
    ) {
      bezierControl = point.offset(new Size(0, 0));
      continue;
    }

    if (bezierControl) {
      result.push({
        curve: [
          point.offset(nextOffset),
          bezierControl,
          point.offset(new Size(0, 0)),
        ],
      });
      bezierControl = undefined;
    } else {
      result.push({ quadCurve: [point.offset(nextOffset), point] });
    }
  }
  return result;
}

function last<T>(items: T[]): T | undefined {
  return items[items.length - 1];
}

function outline(rects: Rect[]): PathCommand[] {
  const commands: PathCommand[] = [];

  // Right
  for (let i = 0; i < rects.length; i++) {
    const rect = rects[i];
    let trY = 0;
    let brY = 0;

    if (i === 0) {
      commands.push({ move: rect.tl });
      trY = rect.tr.y;
    } else {
      const prev = rects[i - 1];
      const offset = prev.br.x > rect.tr.x ? 0 : rect.tr.y - prev.br.y;
      trY = prev.br.y + offset;
    }

    // Top
    commands.push({ line: new Point(rect.tr.x, trY) });

    if (i === rects.length - 1) {
      brY = rect.br.y;
    } else {
      const next = rects[i + 1];
      const offset = next.tr.x > rect.br.x ? next.tr.y - rect.br.y : 0;
      brY = rect.br.y + offset;
    }

    // Right
    commands.push({ line: new Point(rect.br.x, brY) });
  }

  // Left
  for (let i = rects.length - 1; i > -1; i--) {
    const rect = rects[i];
    let tlY = 0;
    let blY = 0;

    if (i === rects.length - 1) {
      blY = rect.bl.y;
    } else {
      const prev = rects[i + 1];
      const offset = prev.bl.x > rect.tl.x ? 0 : prev.tl.y - rect.bl.y;
      blY = rect.bl.y + offset;
    }

    // Bottom
    commands.push({ line: new Point(rect.bl.x, blY) });

    if (i === 0) {
      tlY = rect.tl.y;
    } else {
      const next = rects[i - 1];
      const offset = next.tl.x < rect.tl.x ? next.bl.y - rect.tl.y : 0;
      tlY = rect.tl.y + offset;
    }

    // Left
    commands.push({ line: new Point(rect.bl.x, tlY) });
  }

  return commands;
}
